import { Haptics, ImpactStyle } from '@capacitor/haptics'
import { Capacitor } from '@capacitor/core'
import WindowStore from '@/store/window'
import { WatchStopHandle } from 'vue'

export const touchHold = (action: () => void) => {
  const windowStore = WindowStore()
  const { mouseX, mouseY } = storeToRefs(windowStore)

  const touchTimeout = ref()
  const touchStart = reactive({ x: 0, y: 0 })

  const onTouchStart = () => {
    setTimeout(() => {
      touchStart.x = mouseX.value
      touchStart.y = mouseY.value
    })

    touchTimeout.value = setTimeout(() => {
      vibrate(ImpactStyle.Light)

      action()
    }, 500)
  }

  const onTouchMove = () => {
    if (Math.abs(touchStart.y - mouseY.value) > 5 || Math.abs(touchStart.x - mouseX.value) > 5) onTouchEnd()
  }

  const onTouchEnd = () => {
    if (touchTimeout.value) {
      touchStart.x = 0
      touchStart.y = 0

      clearTimeout(touchTimeout.value)
    }
  }

  return {
    onTouchStart, onTouchEnd, onTouchMove
  }
}

export const touchDrag = (actions: {
  start: () => void,
  end: () => void,
}) => {
  const windowStore = WindowStore()
  const { mouseX, mouseY } = storeToRefs(windowStore)

  const touchTimeout = ref()
  const touchStart = reactive({ x: 0, y: 0 })
  const touchMove = reactive({ x: 0, y: 0 })

  const onTouchStart = () => {
    setTimeout(() => {
      touchStart.x = mouseX.value
      touchStart.y = mouseY.value
    })

    touchTimeout.value = setTimeout(() => {
      vibrate(ImpactStyle.Light)

      actions.start()
    }, 500)
  }

  const onTouchMove = () => {
    touchMove.x = touchStart.x - mouseX.value
    touchMove.y = touchStart.y - mouseY.value
  }

  const onTouchEnd = () => {
    if (touchTimeout.value) {
      touchStart.x = 0
      touchStart.y = 0

      touchMove.x = 0
      touchMove.y = 0

      clearTimeout(touchTimeout.value)
    }

    actions.end()
  }

  return {
    onTouchStart, onTouchEnd, onTouchMove, movement: touchMove
  }
}

export const touchSlide = (action: () => void) => {
  const windowStore = WindowStore()
  const { mouseX, mouseY } = storeToRefs(windowStore)

  const touchStart = reactive({ x: 0, y: 0 })
  const movement = reactive({ x: 0, y: 0 })

  const isDragging = ref(false)

  const onTouchStart = () => {
    isDragging.value = true
  
    setTimeout(() => {
      touchStart.x = mouseX.value
      touchStart.y = mouseY.value
    }, 10)
  }

  const onTouchMove = () => {
    movement.x = Math.min(Math.max(0, mouseX.value - touchStart.x), 50)
  }

  const onTouchEnd = () => {
    isDragging.value = false

    if (movement.x >= 40) action()

    touchStart.x = 0
    touchStart.y = 0

    movement.x = 0
    movement.y = 0
  }

  return {
    onTouchStart, onTouchEnd, onTouchMove, movement, isDragging
  }
}

export const doubleTap = (action: () => void) => {
  const windowStore = WindowStore()
  const { mouseX, mouseY } = storeToRefs(windowStore)

  const tapTimeout = ref()
  const tapStart = reactive({ x: 0, y: 0 })

  const onTap = () => {
    if (tapTimeout.value) {
      clearTimeout(tapTimeout.value)
      tapTimeout.value = null

      if (Math.abs(tapStart.x - mouseX.value) < 30 && Math.abs(tapStart.y - mouseY.value) < 30) {
        action()
      }
    } else {
      setTimeout(() => {
        tapStart.x = mouseX.value
        tapStart.y = mouseY.value
      })

      tapTimeout.value = setTimeout(() => {
        tapStart.x = 0
        tapStart.y = 0
        tapTimeout.value = null
      }, 300)
    }
  }

  return {
    onTap
  }
}

export const vibrate = async (style: ImpactStyle) => {
  const isDisabled = await getCookie('disable-vibrations') as boolean

  if (Capacitor.isPluginAvailable('Haptics') && !isDisabled) Haptics.impact({ style })
}

export const onSlide = (
  $element: Ref<HTMLElement>,
  options: {
    scrollRef: HTMLElement | 'window'
    isDisabled?: Ref<boolean>
    watchersAdd: (createWatcher: () => WatchStopHandle) => void,
    max?: { x: number, y: number },
    min?: { x: number, y: number }
    onRelease: () => void,
  }
) => {
  const windowStore = WindowStore()
  const { mouseX, mouseY } = storeToRefs(windowStore)

  const { pressed: isDragging } = useMousePressed({ target: $element })
  const { y: scrollY } = useScroll(options.scrollRef === 'window' ? window : options.scrollRef)
  const scrollLock = ref(false)
  
  const isDraggingLong = ref(false)

  const startDrag = ref({ x: 0, y: 0 })
  const movement = ref({ x: 0, y: 0 })
  const traveled = ref(0)

  const isTransition = ref(false)
  const isScrolling = ref(false)
  const isValidated = computed(() => traveled.value > 0)
  const scrolled = ref(0)
  const stopTouchMoveListener = ref()

  options.watchersAdd(() => watch(scrollLock, () => {
    const target = options.scrollRef === 'window' ? document.body : options.scrollRef as HTMLElement

    if (!target) return

    if (scrollLock.value) {
      target.classList.add('lockdown')
      if (stopTouchMoveListener.value) stopTouchMoveListener.value()
      
      stopTouchMoveListener.value = useEventListener(target, 'touchmove',
        (e) => { e.preventDefault() },
        { passive: false },
      )
    } else {
      target.classList.remove('lockdown')

      if (stopTouchMoveListener.value) stopTouchMoveListener.value()
    }
  }, { immediate: true }))

  options.watchersAdd(() => watch(isDragging, () => {
    if (options.isDisabled && options.isDisabled.value) return

    if (isDragging.value) {
      setTimeout(() => {
        if (isDragging.value && !(options.isDisabled && options.isDisabled.value)) isDraggingLong.value = true
      }, 150)
    } else {
      isDraggingLong.value = false
    }
  }))

  options.watchersAdd(() => watch([ isDraggingLong, isScrolling ], () => {
    if (isDraggingLong.value) isTransition.value = false
    if (!isDraggingLong.value) onRelease()
  }))

  options.watchersAdd(() => watch(scrollY, () => {
    scrolled.value = scrolled.value + scrollY.value
    isScrolling.value = true
  }))

  options.watchersAdd(() => watchDebounced(scrolled, () => {
    if (!isDragging.value) isScrolling.value = false
  }, { debounce: 500 }))

  options.watchersAdd(() => watchThrottled(mouseX, () => {
    if (options.isDisabled && options.isDisabled.value) return
    if (isScrolling.value || !isDraggingLong.value) return

    if (isDraggingLong.value) {
      if (startDrag.value.x === 0) startDrag.value = { x: mouseX.value, y: mouseY.value }

      movement.value = {
        x: Math.min(options?.min?.x ?? 0, Math.max(mouseX.value - startDrag.value.x, options?.max?.x ?? 0)),
        y: Math.min(options?.min?.y ?? 0, Math.max(mouseY.value - startDrag.value.y, options?.max?.y ?? 0))
      }
      
      traveled.value += Math.abs(movement.value.x)
    }
  }, { throttle: 3 }))

  options.watchersAdd(() => watch(isValidated, () => {
    if (isValidated.value) {
      scrollLock.value = true
    } else {
      scrollLock.value = false
    }
  }))

  const onRelease = () => {
    if ((!isScrolling.value || isValidated.value) && options?.onRelease) options.onRelease()

    if (traveled.value > 0) isTransition.value = true

    startDrag.value = { x: 0, y: 0 }
    scrolled.value = 0
    traveled.value = 0
    movement.value = { x: 0, y: 0 }
    isScrolling.value = false

    setTimeout(() => scrollLock.value = false)

    setTimeout(() => {
      isTransition.value = false
    }, 300)
  }

  const onKill = () => {
    onRelease()

    const target = options.scrollRef === 'window' ? document.body : options.scrollRef as HTMLElement
    
    if (target) target.classList.remove('lockdown')

    if (stopTouchMoveListener.value) stopTouchMoveListener.value()
  }

  return {
    movement,
    isScrolling,
    onKill,
    isTransition,
    isValidated,
  }
}